import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import classNames from 'classnames';
import moment from 'moment';

import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import FormControl from '@material-ui/core/FormControl';
import FormLabel from '@material-ui/core/FormLabel';

import { Field, reduxForm, SubmissionError } from 'redux-form';

import Progress from 'components/common/Progress';
import Button from 'components/common/Button';
import DatepickerField from 'components/fields/DatepickerField';
import TimepickerField from 'components/fields/TimepickerField';
import AutosuggestField from 'components/fields/AutosuggestField';
import CheckboxField from 'components/fields/CheckboxField';

import { requestSearchUsers } from 'redux/modules/users';
import { updateOrderLines } from 'redux/modules/ordersLines';
import { accountLoginFail } from 'redux/modules/account';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';

import dateFormat from 'config/dateFormat';
import timeFormat from 'config/timeFormat';
import timeDateFormat from 'config/timeDateFormat';

const styles = theme => ({
  root: {
    position: 'relative',
    width: 300,
  },
  grid: {
    marginBottom: 10,
    [theme.breakpoints.down('sm')]: {
      marginBottom: 4,
    },
  },
  button: {
    minWidth: 107,
    marginTop: 5,
    marginBottom: 5,
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues);
    if (normalizedValues['Date of Delivery']) {
      if (normalizedValues['Time of Delivery']) {
        const hour = moment(normalizedValues['Time of Delivery'], timeFormat).hour();
        const minute = moment(normalizedValues['Time of Delivery'], timeFormat).minute();
        normalizedValues['Date of Delivery'] = moment(normalizedValues['Date of Delivery'], dateFormat)
          .hour(hour)
          .minute(minute)
          .format(timeDateFormat);
      } else {
        normalizedValues['Date of Delivery'] = moment(normalizedValues['Date of Delivery'], dateFormat)
          .format(timeDateFormat);
      }
    } else {
      delete normalizedValues['Date of Delivery'];
    }
    delete normalizedValues['User Name'];
    delete normalizedValues['Selected User Name'];
    delete normalizedValues['Driver Name'];
    delete normalizedValues['Selected Driver Name'];
    delete normalizedValues['Time of Delivery'];
    delete normalizedValues['Use Date of Service as Date of Delivery'];

    return normalizedValues;
  };
  return new Promise((resolve, reject) => {
    dispatch(updateOrderLines({
      values: props.selectedOrderLines.map(item => ({
        ID: item.ID,
        orderID: item['Order ID'],
        ...props.moveSection === 'Rejected' ? {
          'Is Rejected': true,
        } : {
          ...props.moveSection === 'Intake' ? { 'Workflow State ID': [1] } : {},
          ...props.moveSection === 'Delivery' ? { 'Workflow State ID': [2] } : {},
          ...props.moveSection === 'Billing' ? { 'Workflow State ID': [3] } : {},
          ...props.moveSection === 'Collection' ? { 'Workflow State ID': [4] } : {},
          ...props.moveSection === 'Paid' ? { 'Workflow State ID': [5] } : {},
        },
        ...normalizeValues(values),
        ...values['Use Date of Service as Date of Delivery'] ? {
          'Date of Delivery': `${item['Date of Service From']} 08:00 am`,
        } : {},
      })),
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      throw new SubmissionError(err);
    });
}

function validateForm(values) {
  const errors = {};
  if (!values['Assigned User ID'] || values['Assigned User ID'].length === 0) {
    errors['User Name'] = 'Please, select user from list';
  }
  if (values['Driver Name']
    && values['Driver Name'].length > 0
    && values['Driver Name'] !== values['Selected Driver Name']) {
    errors['Driver Name'] = 'Please, select driver from list';
  }
  return errors;
}

function onChange(values, dispatch, props) {
  if (values['Selected User Name'] !== values['User Name']) {
    props.change('Assigned User ID', '');
  }
  if (values['Selected Driver Name'] !== values['Driver Name']) {
    props.change('Driver ID', '');
  }
}

const OrdersActionsBarMoveForm = ({
  handleSubmit,
  change,
  submit,
  moveSection,
  className,
  submitting,
  actions,
  initialValues,
  classes,
}) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
    className={classNames(className, classes.root)}
  >
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Field
          id="User Name"
          name="User Name"
          label={`On the ${moveSection} order is assigned to:`}
          component={AutosuggestField}
          placeholder="User"
          fullWidth
          renderSuggestionContent={suggestion => (
            `${suggestion['First Name']} ${suggestion['Last Name']}`
          )}
          onSuggestionSelected={(suggestion) => {
            change('Assigned User ID', suggestion.ID);
            change('Selected User Name', `${suggestion['First Name']} ${suggestion['Last Name']}`);
            return `${suggestion['First Name']} ${suggestion['Last Name']}`;
          }}
          fetchSuggestions={({ value, limit, offset }) => (
            value.length > 0
              ? requestSearchUsers({
                filters: [
                  {
                    name: 'Role',
                    operation: 'nin',
                    val: ['Driver', 'System'],
                  },
                  {
                    name: 'Active',
                    operation: 'eq',
                    val: 'true',
                  },
                  {
                    name: 'Full Name',
                    operation: 'startswith',
                    val: value,
                    case_sensitivity: false,
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestSearchUsers({
                filters: [
                  {
                    name: 'Role',
                    operation: 'nin',
                    val: ['Driver', 'System'],
                  },
                  {
                    name: 'Active',
                    operation: 'eq',
                    val: 'true',
                  },
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
          validate={[validate.required]}
        />
      </Grid>
    </Grid>
    {moveSection === 'Delivery' &&
      <Fragment>
        <Grid
          className={classes.grid}
          container
          justify="center"
          spacing={24}
        >
          <Grid
            item
            xs={12}
          >
            <Field
              id="Driver Name"
              name="Driver Name"
              label="Driver"
              component={AutosuggestField}
              placeholder="-Select Driver-"
              fullWidth
              renderSuggestionContent={suggestion => (
                `${suggestion['First Name']} ${suggestion['Last Name']}`
              )}
              onSuggestionSelected={(suggestion) => {
                change('Driver ID', suggestion.ID);
                change('Selected Driver Name', `${suggestion['First Name']} ${suggestion['Last Name']}`);
                return `${suggestion['First Name']} ${suggestion['Last Name']}`;
              }}
              fetchSuggestions={({ value, limit, offset }) => (
                value.length > 0
                  ? requestSearchUsers({
                    filters: [
                      {
                        name: 'Role',
                        operation: 'eq',
                        val: 'Driver',
                      },
                      {
                        name: 'Active',
                        operation: 'eq',
                        val: 'true',
                      },
                      {
                        name: 'Full Name',
                        operation: 'startswith',
                        val: value,
                        case_sensitivity: false,
                      },
                    ],
                    limit,
                    offset,
                  }).then(res => res.data,
                    (err) => {
                      if (err.data.error === 'Need authorisation') {
                        actions.accountLoginFail();
                      }
                      return err;
                    })
                  : requestSearchUsers({
                    filters: [
                      {
                        name: 'Role',
                        operation: 'eq',
                        val: 'Driver',
                      },
                      {
                        name: 'Active',
                        operation: 'eq',
                        val: 'true',
                      },
                    ],
                    limit,
                    offset,
                  }).then(res => res.data,
                    (err) => {
                      if (err.data.error === 'Need authorisation') {
                        actions.accountLoginFail();
                      }
                      return err;
                    })
              )}
            />
          </Grid>
        </Grid>
        {initialValues['Use Date of Service as Date of Delivery'] ? (
          <Grid
            className={classes.grid}
            container
            justify="center"
            spacing={24}
          >
            <Grid
              item
              xs={12}
            >
              <Field
                id="Use Date of Service as Date of Delivery"
                name="Use Date of Service as Date of Delivery"
                label="Use Date of Service as Date of Delivery"
                component={CheckboxField}
              />
            </Grid>
          </Grid>
        ) : (
          <FormControl component="fieldset">
            <FormLabel component="legend">Date and Time of Delivery</FormLabel>
            <Grid
              className={classes.grid}
              container
              justify="center"
              spacing={16}
            >
              <Grid item xs={6}>
                <Field
                  id="Date of Delivery"
                  name="Date of Delivery"
                  component={DatepickerField}
                  validate={[validate.date]}
                  normalize={normalize.date}
                  fullWidth
                  minDate="01/01/1900"
                  maxDate="01/01/2100"
                  dateFormat={dateFormat}
                  placeholder="mm/dd/yyyy"
                  maxLength="10"
                  endIcon="start"
                />
              </Grid>
              <Grid item xs={6}>
                <Field
                  id="Time of Delivery"
                  name="Time of Delivery"
                  component={TimepickerField}
                  validate={[validate.time, validate.timeOfDelivery]}
                  normalize={normalize.time}
                  placeholder="hh:mm am"
                  fullWidth
                  endIcon
                />
              </Grid>
            </Grid>
          </FormControl>
        )}
      </Fragment>
    }
    <Grid
      container
      justify="center"
      spacing={24}
    >
      <Grid
        item
      >
        <Button
          className={classes.button}
          variant="raised"
          color="primary"
          onClick={submit}
        >
          Done
        </Button>
      </Grid>
    </Grid>
    {submitting &&
      <Progress />
    }
  </form>
);

OrdersActionsBarMoveForm.propTypes = {
  moveSection: PropTypes.oneOf([
    'Intake',
    'Delivery',
    'Billing',
    'Collection',
  ]).isRequired,
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  submit: PropTypes.func.isRequired,
  className: PropTypes.string,
  submitting: PropTypes.bool.isRequired,
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    root: PropTypes.string.isRequired,
    grid: PropTypes.string.isRequired,
    button: PropTypes.string.isRequired,
  }).isRequired,
};

OrdersActionsBarMoveForm.defaultProps = {
  className: '',
};

const mapStateToProps = (store, ownProps) => {
  const differentDatesOfService = ownProps.selectedOrderLines.filter(item =>
    item['Date of Service From'] !== ownProps.selectedOrderLines[0]['Date of Service From']).length > 0;
  return {
    initialValues: {
      'User Name': `${store.account['First Name']} ${store.account['Last Name']}`,
      'Selected User Name': `${store.account['First Name']} ${store.account['Last Name']}`,
      'Assigned User ID': store.account.ID,
      ...ownProps.moveSection === 'Delivery'
      && ownProps.selectedOrderLines
      && ownProps.selectedOrderLines.length > 0 ? {
        // ...differentDatesOfService ? {
        //   'Use Date of Service as Date of Delivery': true,
        // } : {
          'Date of Delivery': ownProps.selectedOrderLines[0]['Date of Service From'],
          'Time of Delivery': '08:00 am',
        // }
      } : {},
    },
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(reduxForm({
  onSubmit,
  onChange,
  validate: validateForm,
})(withStyles(styles)(OrdersActionsBarMoveForm)));



// WEBPACK FOOTER //
// ./src/components/workflow/OrdersActionsBar/OrdersActionsBarMoveForm.js